/**
 * ── Sitrep zones + clearance ──
 *
 * The board render needs each authored zone paired with its live clearance, and clearance is a
 * WHOLE-state read (terrain + edges + combatants) that `CombatStage` never receives — so it is
 * resolved here and passed down as one render-only prop, exactly the posture `rooms`/`areaEffects`
 * already take. Memoized on `state`: without it every render would hand `setSitrepZones` a fresh
 * array and rebuild the layer's Graphics for nothing.
 *
 * **`state.sitrep` arrives already redacted, but read what that redaction IS:** `projectSitrepFor`
 * strips the `hidden` FLAG off every zone for a non-GM viewer and leaves the zone itself
 * (`id`/`kind`/`rects` all survive — Recon's players see every CZ, they just don't know which is
 * the True one). So clearance totals are identical for every viewer, and nothing here re-derives
 * visibility. Only `clear`-kind zones get a clearance read — nothing else has ground to tally.
 */
import { useMemo } from "react";
import { sitrepClearance, zoneClearance } from "@lancer-vtt/rules";
import type { useEncounter } from "../../../lib/encounter/useEncounter";
import type { SitrepZoneRender } from "../pixiCombatBoard";

export function useSitrepZones(state: ReturnType<typeof useEncounter>["state"]) {
  const sitrepZones: SitrepZoneRender[] = useMemo(
    () =>
      (state?.sitrep?.zones ?? []).map((zone) => ({
        zone,
        clearance: zone.kind === "clear" && state ? zoneClearance(state, zone) : null,
      })),
    [state],
  );

  // The summed read the SITREP strip shows. Derived in the SAME memo pass as the per-zone board
  // data above (the strip isn't memoized, so letting it compute its own would re-scan every zone
  // cell × every combatant on each render — a token drag renders often). One derivation, two
  // consumers, so the strip and the board can never disagree.
  const sitrepClearanceTotal = useMemo(
    () => (state?.sitrep?.type === "clear" ? sitrepClearance(state, state.sitrep) : null),
    [state],
  );

  return { sitrepZones, sitrepClearanceTotal };
}
